import { createStore } from "vuex";
import config from "@/config";
import auth from "./auth"; // Ensure auth module is imported
import { useDisplay } from "vuetify";
import { getCountries } from "@/api"; // Import getCountries
import { showAlertModal } from "@/utils";

// Allowed country codes to filter
const allowedCodes = [
    "AL",
    "AD",
    "AT",
    "BY",
    "BE",
    "BA",
    "BG",
    "CH",
    "CY",
    "CZ",
    "DE",
    "XK",
    "DK",
    "EE",
    "ES",
    "FI",
    "FR",
    "GR",
    "HR",
    "HU",
    "IE",
    "IS",
    "IT",
    "PL",
    "LI",
    "LT",
    "LU",
    "LV",
    "MC",
    "MD",
    "ME",
    "MK",
    "MT",
    "NL",
    "NO",
    "PT",
    "RO",
    "RS",
    "RU",
    "SE",
    "SI",
    "SK",
    "SM",
    "TR",
    "UA",
    "VA",
    "GB",
];

export default createStore({
    state: {
        Sidebar_drawer: config.Sidebar_drawer,
        mini_sidebar: config.mini_sidebar,
        actTheme: config.actTheme,
        fontTheme: config.fontTheme,
        isMobile: false,
        submitRequested: false,
        filteredCountries: [],
        selectedLanguage: "en",
        formButtons: [],
        alert: {
            show: false,
            message: "",
            type: "",
            duration: null,
        },
        alertQueue: [],
        alertTimeoutId: null,
    },
    getters: {},
    mutations: {
        SET_ALERT(state, { message, type, duration, visible, errorDetails }) {
            state.alert.message = message;
            state.alert.type = type;
            state.alert.duration = duration;
            state.alert.show = visible;
            state.alert.errorDetails = errorDetails || "";
            state.alert.remainingDuration = duration;
        },
        clearCurrentAlert(state) {
            state.alert.show = false;
            state.alert.message = "";
            state.alert.type = "";
            state.alert.duration = null;
            state.alert.remainingDuration = null;
            state.alert.errorDetails = "";

            if (state.alertTimeoutId) {
                clearTimeout(state.alertTimeoutId);
                state.alertTimeoutId = null;
            }
        },
        queueAlert(state, alert) {
            state.alertQueue.push(alert);
        },
        SET_FORM_BUTTONS(state, buttons) {
            state.formButtons = buttons;
        },
        RESET_FORM_BUTTONS(state) {
            state.formButtons = [];
        },
        SET_SIDEBAR_DRAWER(state) {
            state.Sidebar_drawer = !state.Sidebar_drawer;
        },
        SET_MINI_SIDEBAR(state, payload) {
            state.mini_sidebar = payload;
        },
        SET_THEME(state, payload) {
            state.actTheme = payload;
        },
        SET_FONT(state, payload) {
            state.fontTheme = payload;
        },
        SET_IS_MOBILE(state, value) {
            state.isMobile = value;
        },
        REQUEST_SUBMIT(state) {
            state.submitRequested = true;
        },
        RESET_SUBMIT(state) {
            state.submitRequested = false;
        },
        SET_FILTERED_COUNTRIES(state, countries) {
            state.filteredCountries = countries;
        },
    },
    actions: {
        configureAlert({ commit, state, dispatch }, alert) {
            if (state.alertTimeoutId) {
                clearTimeout(state.alertTimeoutId);
                state.alertTimeoutId = null;
            }

            commit("SET_ALERT", alert);

            state.alertTimeoutId = setTimeout(() => {
                commit("clearCurrentAlert");
                dispatch("processNextAlert");
            }, alert.duration);
        },
        processNextAlert({ state, dispatch }) {
            if (state.alertQueue.length > 0) {
                const nextAlert = state.alertQueue.shift();
                if (nextAlert) {
                    if (nextAlert.type !== "danger" && !nextAlert.visible) {
                        nextAlert.visible = true;
                    }
                    dispatch("configureAlert", nextAlert);
                }
            }
        },
        showAlertModal(
            { commit, dispatch, state },
            { message, type, duration, errorDetails }
        ) {
            if (type === "danger") {
                commit("clearCurrentAlert");
                dispatch("configureAlert", {
                    message,
                    type,
                    duration,
                    visible: true,
                    errorDetails,
                });
            } else {
                const currentAlertType = state.alert.type;
                if (currentAlertType === "danger" && state.alert.show) {
                    commit("queueAlert", {
                        message,
                        type,
                        duration,
                        errorDetails,
                    });
                } else {
                    dispatch("configureAlert", {
                        message,
                        type,
                        duration,
                        visible: true,
                        errorDetails,
                    });
                }
            }
        },
        toggleSidebarDrawer({ commit }) {
            commit("SET_SIDEBAR_DRAWER");
        },
        setMiniSidebar({ commit }, payload) {
            commit("SET_MINI_SIDEBAR", payload);
        },
        setTheme({ commit }, payload) {
            commit("SET_THEME", payload);
        },
        setFont({ commit }, payload) {
            commit("SET_FONT", payload);
        },
        updateScreenSize({ commit }) {
            const isMobile = window.matchMedia("(max-width: 960px)").matches; // Matches Vuetify mdAndDown
            commit("SET_IS_MOBILE", isMobile);
        },
        async fetchFilteredCountries({ commit, state }) {
            const lang = state.selectedLanguage;
            const cacheKey = `filteredCountries_${lang}`;

            try {
                // 1. Try loading from cache
                const cached = localStorage.getItem(cacheKey);
                if (cached) {
                    const parsed = JSON.parse(cached);
                    commit("SET_FILTERED_COUNTRIES", parsed);
                    return;
                }

                // 2. Fetch from API if not cached
                const countries = await getCountries(lang);

                // 3. Apply your code filtering
                const filtered = countries.filter((country) =>
                    allowedCodes.includes(country.code)
                );

                // 4. Save to cache
                localStorage.setItem(cacheKey, JSON.stringify(filtered));

                // 5. Commit to Vuex
                commit("SET_FILTERED_COUNTRIES", filtered);
            } catch (error) {
                console.error("Error fetching filtered countries:", error);
                showAlertModal(
                    this.$store,
                    "Error fetching filtered countries",
                    "danger",
                    12000,
                    error
                );
            }
        },
    },

    modules: {
        auth, // Register auth module properly
    },
});
